import { FunctionsHttpError } from '@supabase/supabase-js';
import { supabase } from './api';

/** Only enforce the 20-day lockout for accounts created on/after this feature ship date. */
export const EMAIL_CONFIRMATION_ENFORCEMENT_START = '2026-09-03T00:00:00.000Z';
export const EMAIL_CONFIRMATION_GRACE_DAYS = 20;
export const EMAIL_CONFIRMATION_RESEND_COOLDOWN_MS = 60_000;

export function needsEmailConfirmation(user) {
  return Boolean(user && !user.email_confirmed_at);
}

/**
 * Locked out of the app (session stays valid) when email is unconfirmed past the
 * grace window — but only for accounts created on/after the feature ship date.
 */
export function isEmailConfirmationLocked(user) {
  if (!needsEmailConfirmation(user) || !user.created_at) return false;
  const createdMs = new Date(user.created_at).getTime();
  const floorMs = new Date(EMAIL_CONFIRMATION_ENFORCEMENT_START).getTime();
  if (Number.isNaN(createdMs) || createdMs < floorMs) return false;
  const deadlineMs = createdMs + EMAIL_CONFIRMATION_GRACE_DAYS * 24 * 60 * 60 * 1000;
  return Date.now() > deadlineMs;
}

async function readFunctionError(error, fallback) {
  let msg = error?.message || fallback;
  if (error instanceof FunctionsHttpError && error.context?.json) {
    try {
      const errBody = await error.context.json();
      if (errBody?.error && typeof errBody.error === 'string') msg = errBody.error;
      else if (errBody?.message && typeof errBody.message === 'string') msg = errBody.message;
    } catch {
      /* keep msg */
    }
  }
  return msg;
}

export async function sendEmailConfirmation() {
  if (!supabase) throw new Error('Email confirmation is not available.');
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session) throw new Error('No active session');

  const { data, error } = await supabase.functions.invoke('send-email-confirmation', {
    body: {},
    headers: { Authorization: `Bearer ${session.access_token}` },
  });

  if (error) {
    throw new Error(await readFunctionError(error, 'Could not send confirmation email'));
  }
  if (data?.error) {
    throw new Error(typeof data.error === 'string' ? data.error : 'Could not send confirmation email');
  }
  return data;
}

export async function confirmEmailWithToken(token) {
  if (!supabase) throw new Error('Email confirmation is not available.');
  const raw = typeof token === 'string' ? token.trim() : '';
  if (!raw) throw new Error('Confirmation token is missing');

  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session) throw new Error('No active session');

  const { data, error } = await supabase.functions.invoke('confirm-email', {
    body: { token: raw },
    headers: { Authorization: `Bearer ${session.access_token}` },
  });

  if (error) {
    throw new Error(await readFunctionError(error, 'Could not confirm email'));
  }
  if (data?.error) {
    throw new Error(typeof data.error === 'string' ? data.error : 'Could not confirm email');
  }
  if (!data?.success) {
    throw new Error('Could not confirm email');
  }
  return data;
}
